import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  message,
  Table,
  Switch,
  Modal,
  Tag,
  Radio,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import WelcomePackageFields, {
  toFormValues,
  toPayload,
} from "./WelcomePackageFields";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
const MAX_ACTIVE_PACKAGES = 3;

const INITIAL_VALUES = {
  amount: 20,
  promoRateDurationDays: 7,
  trackingDurationDays: 7,
  freeFlyers: 0,
  isPopular: false,
  isActive: true,
};

const WelcomePackage = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [packages, setPackages] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [showActive, setShowActive] = useState(true);
  const [pricingPackageOptions, setPricingPackageOptions] = useState([]);
  const [feePackageOptions, setFeePackageOptions] = useState([]);
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [editingPackage, setEditingPackage] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchPackages = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const res = await axios.get("/api/welcome-packages", {
        params: {
          isActive: showActive,
          limit: pagination.limit,
          page: pagination.currentPage,
        },
      });
      setPackages(res.data.data);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, showActive]);

  const fetchActiveCount = useCallback(async () => {
    try {
      const res = await axios.get("/api/welcome-packages", {
        params: { isActive: true, limit: 100 },
      });
      setActiveCount(res.data.pagination?.totalSize ?? res.data.data.length);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  }, []);

  const fetchPackageOptions = useCallback(async () => {
    try {
      const [domestic, fee] = await Promise.all([
        axios.get("/api/pricing-package-labels", {
          params: { category: "domestic", limit: 1000 },
        }),
        axios.get("/api/pricing-package-labels", {
          params: { category: "fee", limit: 1000 },
        }),
      ]);
      setPricingPackageOptions(domestic.data.data || []);
      setFeePackageOptions(fee.data.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    fetchActiveCount();
    fetchPackageOptions();
  }, [fetchActiveCount, fetchPackageOptions]);

  const refresh = () => {
    fetchPackages();
    fetchActiveCount();
  };

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      await axios.post("/api/welcome-packages", toPayload(values));
      message.success("Welcome package created");
      form.resetFields();
      refresh();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const updateHandler = async (packageId, values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      await axios.patch(`/api/welcome-packages/${packageId}`, toPayload(values));
      message.success("Welcome package updated");
      setEditingPackage(null);
      refresh();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const toggleStatusHandler = async (packageId, isActive) => {
    try {
      await axios.patch(`/api/welcome-packages/${packageId}/toggle-status`, {
        isActive,
      });
      message.success("Status updated");
      refresh();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const confirmDeactivate = (record) => {
    Modal.confirm({
      title: `Deactivate ${record.name?.en}`,
      content:
        "New top-ups will stop qualifying for this package. Merchants already granted it keep everything they have.",
      okText: "Deactivate",
      okButtonProps: { danger: true },
      onOk: () => toggleStatusHandler(record.objectId, false),
    });
  };

  const handleActiveToggle = (e) => {
    setShowActive(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (record) => {
    setEditingPackage(record);
    editForm.setFieldsValue(toFormValues(record));
  };

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const canCreate = permissions?.includes(PERMISSIONS.CREATE_WELCOME_PACKAGE);
  const canUpdate = permissions?.includes(PERMISSIONS.UPDATE_WELCOME_PACKAGE);
  const canToggle = permissions?.includes(
    PERMISSIONS.TOGGLE_WELCOME_PACKAGE_STATUS
  );
  const capReached = activeCount >= MAX_ACTIVE_PACKAGES;

  const columns = [
    {
      title: "Package",
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (value, record) => (
        <>
          <div>{value?.en}</div>
          <Tag>{record.key}</Tag>
          {record.isPopular && <Tag color="gold">Popular</Tag>}
        </>
      ),
    },
    {
      title: "Qualifying top-up",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (value) => `RM ${value}`,
    },
    {
      title: "Pricing package",
      dataIndex: "pricingPackage",
      key: "pricingPackage",
      width: 160,
    },
    {
      title: "Promo rate (days)",
      dataIndex: "promoRateDurationDays",
      key: "promoRateDurationDays",
      width: 150,
    },
    {
      title: "Fee package",
      dataIndex: "feePackage",
      key: "feePackage",
      width: 140,
    },
    {
      title: "Tracking (days)",
      dataIndex: "trackingDurationDays",
      key: "trackingDurationDays",
      width: 140,
    },
    {
      title: "Free flyers",
      dataIndex: "freeFlyers",
      key: "freeFlyers",
      width: 120,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (value, record) =>
        canToggle ? (
          <Switch
            checked={value}
            disabled={!value && capReached}
            onChange={(checked) =>
              checked
                ? toggleStatusHandler(record.objectId, true)
                : confirmDeactivate(record)
            }
          />
        ) : value ? (
          "Yes"
        ) : (
          "No"
        ),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) =>
        canUpdate && (
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Welcome Package Control"
        childRoutes={[
          { path: "/welcome-packages", breadcrumbName: "Welcome Package Control" },
        ]}
      />

      {canCreate && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ marginBottom: 16 }}
        >
          <Collapse.Panel header="Create new welcome package" key="create">
            <p style={{ color: "#888" }}>
              New users get the package with the highest qualifying amount their
              first top-up reaches. Up to {MAX_ACTIVE_PACKAGES} packages can be
              active at a time ({activeCount} active now). Merchants already on a
              package keep the values they were granted.
            </p>
            <Form
              form={form}
              layout="vertical"
              onFinish={createHandler}
              initialValues={INITIAL_VALUES}
            >
              <WelcomePackageFields
                pricingPackageOptions={pricingPackageOptions}
                feePackageOptions={feePackageOptions}
              />
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const stagingInactive = form.getFieldValue("isActive") === false;
                  const blocked = capReached && !stagingInactive;

                  return (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading.create}
                      disabled={blocked}
                    >
                      {blocked
                        ? `${MAX_ACTIVE_PACKAGES} packages already active`
                        : "Create package"}
                    </Button>
                  );
                }}
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Row justify="start" style={{ marginTop: 16, marginBottom: 8 }}>
        <Col>
          <Radio.Group
            value={showActive}
            onChange={handleActiveToggle}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={true}>Active</Radio.Button>
            <Radio.Button value={false}>Inactive</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={packages}
        pagination={false}
        loading={loading.index}
        scroll={{ x: true }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Modal
        title={`Edit ${editingPackage?.name?.en || "welcome package"}`}
        visible={!!editingPackage}
        width={900}
        onCancel={() => setEditingPackage(null)}
        onOk={() => {
          editForm.validateFields().then((values) => {
            updateHandler(editingPackage.objectId, values);
          });
        }}
        confirmLoading={loading.update}
      >
        <Form form={editForm} layout="vertical">
          <WelcomePackageFields
            pricingPackageOptions={pricingPackageOptions}
            feePackageOptions={feePackageOptions}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default WelcomePackage;
